// LeadStatusGuardProvider — centralises the rule "a lead can only become
// VIEWING_SCHEDULED when it has at least one CONFIRMED (SCHEDULED) viewing".
//
// Surfaces shared by the drag-end handler on the kanban and the in-card
// <LeadStatusSelect> dropdown. When a caller requests the transition to
// VIEWING_SCHEDULED:
//   - the lead already has a confirmed viewing → patch the status directly.
//   - a request is already PENDING → inform and do nothing; the backend
//     auto-advances the lead to VIEWING_SCHEDULED once the Source confirms.
//   - no viewing yet → open the "Yêu cầu xem nhà" request form (the viewing
//     starts PENDING, so the lead is NOT moved now). On cancel → nothing.
//
// All other transitions short-circuit to a direct patch.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { X } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { localizeError } from "@/lib/errorMessages";
import {
  appointmentKeys,
  toAppointment,
} from "@/features/appointment/api";
import { apiRequest } from "@/lib/api-client";
import { AppointmentForm } from "@/features/appointment/components/appointment-form";
import { usePatchLead } from "@/features/lead/api";
import { LEAD_STATUS_LABELS } from "@/features/lead/constants";
import type { Lead, LeadStatus } from "@/features/lead/types";
import type {
  Appointment,
  AppointmentListQuery,
  AppointmentWire,
} from "@/features/appointment/types";
import { useMediaQuery } from "@/features/lead/hooks/use-media-query";

interface LeadStatusGuardValue {
  /** Whether a status patch is currently in flight. Both the kanban-level
   *  drag handler and the in-card dropdown can read this to disable
   *  interactions. */
  isPending: boolean;
  /**
   * Request a status transition for a lead. The provider decides whether
   * to short-circuit to a direct patch (any status other than
   * VIEWING_SCHEDULED, or a VIEWING_SCHEDULED transition where the lead
   * already has a non-cancelled appointment) or to open the create-
   * appointment modal first.
   */
  requestStatusChange: (lead: Lead, nextStatus: LeadStatus) => void;
}

const LeadStatusGuardContext = createContext<LeadStatusGuardValue | null>(null);

interface PendingViewing {
  lead: Lead;
}

/**
 * Fetcher used by both the cache pre-check and the explicit
 * fetchQuery — the AppointmentForm's `useAppointments` and the appointment
 * detail sheets share the same `appointmentKeys.list` factory, so a hit
 * here also warms their caches.
 */
async function fetchAppointmentsForLead(leadId: number): Promise<Appointment[]> {
  const wires = await apiRequest<AppointmentWire[]>(
    `/appointments?leadId=${leadId}`,
  );
  return wires.map(toAppointment);
}

export function LeadStatusGuardProvider({ children }: { children: ReactNode }) {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const queryClient = useQueryClient();
  const patchMutation = usePatchLead();
  const [pendingViewing, setPendingViewing] = useState<PendingViewing | null>(
    null,
  );

  const patchStatus = useCallback(
    (lead: Lead, next: LeadStatus) => {
      patchMutation.mutate(
        { id: lead.id, patch: { status: next } },
        {
          onSuccess: () => {
            toast.success(`Đã chuyển sang ${LEAD_STATUS_LABELS[next]}`, {
              description: `${lead.customer.fullName} · vừa xong`,
            });
          },
          onError: (error) => toast.error(localizeError(error)),
        },
      );
    },
    [patchMutation],
  );

  const requestStatusChange = useCallback(
    (lead: Lead, nextStatus: LeadStatus) => {
      if (nextStatus === lead.status) return;

      if (nextStatus !== "VIEWING_SCHEDULED") {
        patchStatus(lead, nextStatus);
        return;
      }

      // VIEWING_SCHEDULED branch — only a CONFIRMED viewing lets the lead
      // through; a still-pending request blocks a duplicate; an empty slate
      // opens the request form.
      const query: AppointmentListQuery = { leadId: lead.id };
      const queryKey = appointmentKeys.list(query);
      void (async () => {
        try {
          const appointments = await queryClient.fetchQuery({
            queryKey,
            queryFn: () => fetchAppointmentsForLead(lead.id),
          });
          const hasConfirmed = appointments.some(
            (a) => a.status === "SCHEDULED",
          );
          if (hasConfirmed) {
            patchStatus(lead, nextStatus);
            return;
          }
          const hasPending = appointments.some(
            (a) =>
              a.status === "PENDING_SOURCE" || a.status === "PENDING_AGENT",
          );
          if (hasPending) {
            toast.info(
              "Đã có yêu cầu xem nhà đang chờ xác nhận. Lead sẽ tự chuyển khi nguồn xác nhận.",
            );
            return;
          }
          setPendingViewing({ lead });
        } catch (error) {
          // Fall through to opening the modal — the user can still create
          // an appointment; the worst case is the modal opens unnecessarily.
          // The toast surfaces the underlying error so they know.
          toast.error(localizeError(error));
          setPendingViewing({ lead });
        }
      })();
    },
    [patchStatus, queryClient],
  );

  const handleClose = useCallback(() => {
    setPendingViewing(null);
  }, []);

  // The request opens a PENDING viewing — the lead is NOT advanced here. The
  // backend auto-advances it to VIEWING_SCHEDULED once the Source confirms, and
  // the request already invalidated the lead/list caches, so we just close.
  const handleAppointmentSuccess = useCallback(() => {
    setPendingViewing(null);
  }, []);

  const value = useMemo<LeadStatusGuardValue>(
    () => ({ isPending: patchMutation.isPending, requestStatusChange }),
    [patchMutation.isPending, requestStatusChange],
  );

  return (
    <LeadStatusGuardContext.Provider value={value}>
      {children}
      <PendingViewingSurface
        pending={pendingViewing}
        isDesktop={isDesktop}
        onClose={handleClose}
        onSuccess={handleAppointmentSuccess}
      />
    </LeadStatusGuardContext.Provider>
  );
}

/**
 * Hook for callers that want to drive a guarded status change.
 *
 * @throws if used outside a {@link LeadStatusGuardProvider}.
 */
export function useLeadStatusGuard(): LeadStatusGuardValue {
  const ctx = useContext(LeadStatusGuardContext);
  if (ctx === null) {
    throw new Error(
      "useLeadStatusGuard must be used inside a <LeadStatusGuardProvider>",
    );
  }
  return ctx;
}

interface PendingViewingSurfaceProps {
  pending: PendingViewing | null;
  isDesktop: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Desktop Dialog + mobile Sheet wrapper around {@link AppointmentForm} (create
 * mode), prefilled with the lead (and its property, if any). The viewing opens
 * PENDING; the lead advances automatically once the Source confirms.
 */
function PendingViewingSurface({
  pending,
  isDesktop,
  onClose,
  onSuccess,
}: PendingViewingSurfaceProps) {
  if (pending === null) return null;
  const { lead } = pending;
  const open = true;
  const propertyName =
    lead.interestedPropertyBuildingName ??
    lead.interestedPropertyInternalCode ??
    null;
  const body = (
    <div className="px-5 py-4">
      <AppointmentForm
        mode="create"
        leadContext
        prefill={{
          leadId: lead.id,
          propertyId: lead.interestedPropertyId,
          ...(propertyName !== null ? { propertyName } : {}),
          ...(lead.assignedBrokerId !== null
            ? { brokerId: lead.assignedBrokerId }
            : {}),
        }}
        onSuccess={onSuccess}
        onCancel={onClose}
      />
    </div>
  );
  const closeButton = (
    <button
      type="button"
      onClick={onClose}
      aria-label="Đóng"
      className="flex size-8 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground hover:bg-muted"
    >
      <X aria-hidden="true" className="size-3.5" />
    </button>
  );
  const header = (
    <div className="flex items-center justify-between border-b border-border px-5 py-4">
      <div>
        <p className="font-mono text-[12px] text-muted-foreground">
          {lead.customer.fullName}
        </p>
        <p className="mt-0.5 text-[16px] font-bold tracking-tight">
          Yêu cầu xem nhà
        </p>
      </div>
      {closeButton}
    </div>
  );
  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
        <DialogContent
          className="gap-0 overflow-hidden p-0 sm:max-w-[520px]"
          showCloseButton={false}
        >
          <DialogTitle className="sr-only">Yêu cầu xem nhà</DialogTitle>
          {header}
          {body}
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Sheet open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <SheetContent
        side="bottom"
        className="max-h-[90vh] gap-0 overflow-y-auto p-0"
        showCloseButton={false}
      >
        <SheetTitle className="sr-only">Yêu cầu xem nhà</SheetTitle>
        {header}
        {body}
      </SheetContent>
    </Sheet>
  );
}
